import { FC, PropsWithChildren, ReactNode, useCallback, useEffect, useRef } from 'react'

import { SearchFieldProps } from '@ui/SearchField/SearchField'
import { Direction } from '@ui/common/layout.types'

import { useWindowResize } from '@utils/domUtils'

import { DropdownContent } from './DropdownContent/DropdownContent'

type ModalRenderContext = {
  search: string
  selectedIds: string[]
  toggle: (id: string) => void
  setSelectedIds: (ids: string[]) => void
}

export interface DropdownProps {
  direction?: Direction
  distance?: string
  isPagination?: boolean
  isMultiSelect?: boolean
  disabled?: boolean
  isOpen: boolean
  content: ReactNode
  setIsOpen: (isOpen: boolean) => void
  isWidthFixed?: boolean
  label?: string
  modalHeaderSearch?: SearchFieldProps
  modalFooter?: ReactNode | ((close: () => void) => ReactNode)
  modalCanSearch?: boolean
  modalInitialSelectedIds?: string[]
  onApplyOnClose?: (selectedIds: string[]) => void
  modalRender?: (ctx: ModalRenderContext) => ReactNode
  onModalSearchChange?: (value: string) => void
}

const useOnESCEvent = (isOpen: boolean, onClose: () => void) => {
  useEffect(() => {
    if (!isOpen) return

    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }

    document.addEventListener('keydown', handleEscKey)
    return () => {
      document.removeEventListener('keydown', handleEscKey)
    }
  }, [isOpen, onClose])
}

export const Dropdown: FC<PropsWithChildren<DropdownProps>> = (props) => {
  const {
    children,
    isOpen,
    isMultiSelect,
    isPagination,
    direction = 'top',
    content,
    setIsOpen,
    disabled = false,
    isWidthFixed = false,
    label,
    modalHeaderSearch,
    modalFooter,
    modalCanSearch,
    modalInitialSelectedIds,
    onApplyOnClose,
    modalRender,
    onModalSearchChange,
  } = props

  const containerRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLDivElement>(null)
  const isMobile = useWindowResize()

  const onClose = useCallback(() => setIsOpen(false), [setIsOpen])

  useOnESCEvent(!isMobile && isOpen, onClose)

  const triggerWidth = triggerRef?.current?.getBoundingClientRect().width

  return (
    <div ref={triggerRef} onClick={() => !disabled && !isMultiSelect && setIsOpen(!isOpen)}>
      {children}
      <DropdownContent
        label={label}
        isOpen={isOpen}
        direction={direction}
        onClose={onClose}
        isMultiSelect={isMultiSelect}
        ref={containerRef}
        // TODO: it's hack
        style={{
          width: isWidthFixed ? triggerWidth : undefined,
          maxWidth: isWidthFixed ? triggerWidth : undefined,
        }}
        isPagination={isPagination}
        triggerElement={triggerRef.current}
        modalHeaderSearch={modalHeaderSearch}
        modalFooter={modalFooter}
        modalCanSearch={modalCanSearch}
        modalInitialSelectedIds={modalInitialSelectedIds}
        onApplyOnClose={onApplyOnClose}
        modalRender={modalRender}
        onModalSearchChange={onModalSearchChange}
      >
        {content}
      </DropdownContent>
    </div>
  )
}
